import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormControl, FormArray } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { ErrorStateMatcher } from '@angular/material/core';
import { ImmediateErrorStateMatcher } from 'app/shared/utils/immediate-error-state-matcher';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';

// Componentes compartidos
import { ToggleOptionComponent } from 'app/shared/components/form-ui/toggle-option/toggle-option.component';
import { FormSectionComponent } from 'app/shared/components/form-ui/form-section/form-section.component';

// Interfaces y Utils
import { UserFieldConfigAttributes } from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-config/user-field-config.entity';
import { UserFieldValueOptionAttributes } from '@albatrosdeveloper/ave-models-package/lib/schemas/fields-catalog/user-field-catalog/user-field-value-option/user-field-value-option.entity';
import { buildValidatorsForField, CatalogFieldGroup, FieldConfig, FieldGroup, FieldRendererComponent, getInitialValueForField, groupFieldsBySection, GroupRendererComponent, SelectOption } from 'app/shared/components/dynamic-forms';
import ShortCompanyAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/company/company-short/company.short.entity';
import { GroupRendererUserFieldsComponent } from 'app/shared/components/dynamic-forms/renderers/group-rendered-user-fields/group-rendered-user-fields.component';

interface GroupedFields {
  groupName: string;
  groupPosition: number;
  icon: string;
  iconColor: string;
  fields: UserFieldConfigAttributes[];
}

@Component({
  selector: 'app-user-fields-configuration',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatSelectModule, MatIconModule, MatButtonModule, MatDialogModule,
    MatDatepickerModule, ToggleOptionComponent,
    NgxMatSelectSearchModule, FormSectionComponent, MatTooltipModule, GroupRendererUserFieldsComponent, FieldRendererComponent
  ],
  providers: [
    { provide: ErrorStateMatcher, useClass: ImmediateErrorStateMatcher }
  ],
  templateUrl: './user-fields-configuration.component.html',
  styles: [`
    :host ::ng-deep app-form-section {
      display: block;
      margin-bottom: 0.2rem;
    }
    .file-input-error {
      border-color: #ef4444 !important;
      background-color: #fef2f2;
    }
  `]
})
export class UserFieldsConfigurationComponent implements OnChanges, OnDestroy {

  @Input() fields: UserFieldConfigAttributes[] = [];
  @Input() groups: CatalogFieldGroup[] = [];
  @Input() model: Record<string, any> = {};
  @Input() isSubForm = false;

  @Input() userFieldsForm: FormGroup;
  private valueChangesSub?: Subscription;


  fieldConfigList = signal<FieldConfig[]>([]);
  fieldGroups = signal<FieldGroup[]>([]);

  formSubmitted = signal(false);
  isSubmitting = signal(false);
  companyList = signal<ShortCompanyAttributes[]>([]);

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<any, any>,
  ) {
    this.userFieldsForm = this.fb.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields'] || changes['model']) {
      this.chargeData();
    }
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
  }

  private chargeData(): void {
    this.fieldConfigList.set(this.fields as any);
    this.fieldGroups.set(groupFieldsBySection(this.fields as any, this.groups));

    this.buildFormFromConfig(this.fields as any);
  }

  externalOptionsMap = computed(() => {
    const map = new Map<string, SelectOption[]>();

    map.set('company', this.companyList().map(c => ({
      code: c._id.toString(),
      value: c.name
    })));

    return map;
  });

  private buildFormFromConfig(fields: FieldConfig[]): void {
    if (!this.userFieldsForm) return;

    fields.forEach(field => {
      const rawKey = field.fieldName ?? field.name;
      const controlKey = typeof rawKey === 'string' ? rawKey.trim() : '';
      if (!controlKey) return;

      if (!this.userFieldsForm.contains(controlKey)) {
        const initialValue = getInitialValueForField(field);
        const validators = buildValidatorsForField(field);
        this.userFieldsForm.addControl(
          controlKey,
          new FormControl(initialValue, validators)
        );
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({ success: false });
  }

  onClose(): void {
    this.dialogRef.close({ success: false });
  }
}